<template>
    <div class="modal fade" ref="pricingTableModal" tabindex="-1" aria-labelledby="pricingTableModalLabel"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-scrollable" style="max-width: 95vw;">
            <div class="modal-content" style="max-height: 80vh;">
                <div class="modal-header pricing-modal-header">
                    <h5 class="modal-title" id="pricingTableModalLabel">
                        Contract Pricing Table
                        <span class="product-count-badge ms-2">
                            <i class="fa-solid fa-cube"></i>
                            {{ parentProductCount }} {{ parentProductCount === 1 ? 'Product' : 'Products' }}
                        </span>
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body" style="padding: 15px 20px; overflow-y: auto;">
                    <SimpleLoader v-if="isLoading"></SimpleLoader>
                    <div v-if="!isLoading" class="pricing-two-column-layout">
                        <!-- LEFT COLUMN: Selected Products Table -->
                        <div class="pricing-left-column">
                            <div class="d-flex justify-content-end mb-2">
                                <button type="button" class="btn btn-sm btn-outline-info" @click.stop="addHeading(productList)" title="Add Section Heading">
                                    <i class="fa-solid fa-heading me-1"></i> Add Heading
                                </button>
                            </div>
                            <table class="product-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Description</th>
                                        <th>Notes</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                        <th>Per Item Discount</th>
                                        <th>Total Discount</th>
                                        <th>Total AUD</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(item, index) in productList" :key="item.id || index">
                                        <!-- HEADING ROW -->
                                        <tr v-if="item.is_heading" class="heading-row" style="background-color: #f1f5f9;">
                                            <td colspan="8" style="padding: 8px 12px; border-top: 2px solid #00bcd4; border-bottom: 2px solid #00bcd4;">
                                                <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
                                                    <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                                                        <div class="reorder-btn-group">
                                                            <button type="button" class="btn-reorder" :disabled="!canMoveUp(index, productList)" @click.stop="moveItemUp(index, productList)" title="Move Up">
                                                                <i class="fa-solid fa-chevron-up"></i>
                                                            </button>
                                                            <button type="button" class="btn-reorder" :disabled="!canMoveDown(index, productList)" @click.stop="moveItemDown(index, productList)" title="Move Down">
                                                                <i class="fa-solid fa-chevron-down"></i>
                                                            </button>
                                                        </div>
                                                        <i class="fa-solid fa-heading text-vertex-blue" style="font-size: 1rem;"></i>
                                                        <i class="fa-solid fa-times remove-icon"
                                                            title="Remove Heading"
                                                            @click.stop="removeHeading(index, productList)"></i>
                                                        <strong v-if="!item.isEditingProduct"
                                                            style="cursor: pointer; font-size: 1rem; color: #1e293b;"
                                                            @click.stop="startEdit(index, 'product')">
                                                            {{ item.product || 'Section Heading' }}
                                                            <i class="fa-solid fa-pencil text-muted ms-2" style="font-size: 0.75rem;"></i>
                                                        </strong>
                                                        <input v-else v-focus type="text" v-model="item.product"
                                                            class="form-control form-control-sm heading-name-input"
                                                            style="width: 250px; display: inline-block;"
                                                            @click.stop
                                                            @blur="endEdit(item, 'product')"
                                                            @keyup.enter="endEdit(item, 'product')" />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>

                                        <!-- REGULAR PRODUCT ROW -->
                                        <tr v-else>
                                            <td class="product-name-cell" :title="item.product"
                                                @click.stop="startEdit(index, 'product')">
                                                <div style="display: flex; align-items: center; gap: 8px;">
                                                    <div class="reorder-btn-group">
                                                        <button type="button" class="btn-reorder" :disabled="!canMoveUp(index, productList)" @click.stop="moveItemUp(index, productList)" title="Move Up">
                                                            <i class="fa-solid fa-chevron-up"></i>
                                                        </button>
                                                        <button type="button" class="btn-reorder" :disabled="!canMoveDown(index, productList)" @click.stop="moveItemDown(index, productList)" title="Move Down">
                                                            <i class="fa-solid fa-chevron-down"></i>
                                                        </button>
                                                    </div>
                                                    <i class="fa-solid fa-times remove-icon" title="Remove Product"
                                                        @click.stop="removeProduct(index)"></i>
                                                    <strong v-if="!item.isEditingProduct">{{ item.product }}</strong>
                                                    <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                                                        @click.stop
                                                        @blur="endEdit(item, 'product')"
                                                        @keyup.enter="endEdit(item, 'product')" />
                                                </div>
                                            </td>
                                            <td :title="item.description" @click.stop="startEdit(index, 'description')">
                                                <span v-if="!item.isEditingDescription">{{ truncateText(item.description, 40) }}</span>
                                                <input v-else type="text" v-model="item.description"
                                                    @blur="endEdit(item, 'description')"
                                                    @keyup.enter="endEdit(item, 'description')" />
                                            </td>
                                            <td :title="item.remarks" @click.stop="startEdit(index, 'remarks')">
                                                <span v-if="!item.isEditingRemarks">{{ truncateText(item.remarks, 40) }}</span>
                                                <input v-else class="remarks-input" type="text" v-model="item.remarks"
                                                    @blur="endEdit(item, 'remarks')" maxlength="254"
                                                    @keyup.enter="endEdit(item, 'remarks')" />
                                                <span v-if="item.isEditingRemarks" class="char-counter">
                                                    {{ (item.remarks || '').length }}/254
                                                </span>
                                            </td>
                                            <td @click.stop="startEdit(index, 'quantity')">
                                                <span v-if="!item.isEditingQuantity">{{ item.quantity }}</span>
                                                <input v-else type="number" v-model.number="item.quantity"
                                                    @blur="endEdit(item, 'quantity')"
                                                    @keyup.enter="endEdit(item, 'quantity')" />
                                            </td>
                                            <td @click.stop="startEdit(index, 'pricing')">
                                                <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                                                <input v-else type="number" v-model.number="item.pricing"
                                                    @blur="endEdit(item, 'pricing')"
                                                    @keyup.enter="endEdit(item, 'pricing')" />
                                            </td>
                                            <td @click.stop="startEdit(index, 'discount')">
                                                <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                                                <input v-else type="number" v-model.number="item.discount"
                                                    @blur="endEdit(item, 'discount')"
                                                    @keyup.enter="endEdit(item, 'discount')" />
                                            </td>
                                            <td>{{ formatCurrency(calculateTotalDiscount(item)) }}</td>
                                            <td>{{ formatCurrency(calculateTotal(item)) }}</td>
                                        </tr>
                                    </template>
                                </tbody>
                                <tfoot v-if="productList && productList.length > 0" class="pricing-summary-footer">
                                    <tr>
                                        <td colspan="7" class="summary-label text-muted">Subtotal (Excl. GST):</td>
                                        <td class="summary-value">{{ formatCurrency(calculateSubTotalPrice) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="7" class="summary-label text-muted" :title="'Goods and Services Tax'">GST (10%):</td>
                                        <td class="summary-value">{{ formatCurrency(calculateTotalGST) }}</td>
                                    </tr>
                                    <tr class="total-row">
                                        <td colspan="7" class="summary-label total-label">TOTAL (Incl. GST):</td>
                                        <td class="summary-value total-value">{{ formatCurrency(calculateTotalPrice) }}</td>
                                    </tr>
                                    <tr>
                                        <td colspan="8" style="padding: 12px; background-color: #f9f9f9;">
                                            <div style="display: flex; justify-content: flex-end; gap: 10px;">
                                                <button class="button-select rebuild-schedule-btn" @click.stop="rebuildSchedule()"
                                                    :disabled="!hasPricingTableChanges">
                                                    <i class="fa-solid fa-calendar-days"></i> &nbsp; Rebuild Schedule
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <!-- RIGHT COLUMN: Available Products Selection -->
                        <div class="pricing-right-column">
                            <div style="padding: 10px;">
                                <input type="text" v-model="searchQuery" placeholder="Search products..." class="product-search-input">
                                <div class="available-count-label">
                                    <i class="fa-solid fa-list"></i>
                                    {{ filteredProducts.length }} of {{ availableProducts.length }} Available
                                </div>
                                <div style="max-height: 70vh; overflow-y: auto;">
                                    <table class="product-selection-table">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Price</th>
                                                <th style="width: 50px; text-align: center;"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(product, index) in filteredProducts" :key="index">
                                                <td :title="product.name" style="color: #00bcd4"><strong>{{ product.name }}</strong></td>
                                                <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                                <td style="width: 50px; text-align: center; padding: 5px;">
                                                    <button title="Add product" class="button-select continue add-btn-small"
                                                        @click.stop="selectProduct(product)"><i class="fa-solid fa-plus"></i></button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import SimpleLoader from '@/components/Utilities/SimpleLoader.vue';
import { Modal } from 'bootstrap';
import { isEmpty, log } from '@/plugins/helper.js';

export default {
    name: 'PricingTableModal',
    components: {
        SimpleLoader,
    },
    directives: {
        focus: {
            mounted(el) {
                el.focus();
            }
        }
    },
    props: {
        setItems: {
            type: Object,
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update-pricing-table', 'save-contract', 'has-changes', 'rebuild-schedule'],
    data() {
        return {
            pricingTableModal: null,
            isSelectingProduct: false,
            pricingOptionCount: 1,
            availableProducts: [],
            tempAvailableProducts: [],
            productListOptions: [],
            productList: [],
            newProductIndex: -1,
            isCharityLead: false,
            hasPricingTableChanges: false,
            needsScheduleRebuild: false,
            searchQuery: '',
        }
    },
    computed: {
        parentProductCount() {
            return this.productList.filter(p => !p.is_heading).length;
        },
        calculateSubTotalPrice() {
            return this.productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = (parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0);
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalPrice() {
            return this.productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        calculateTotalGST() {
            return this.productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return (total + productTotal);
            }, 0).toFixed(2);
        },

        filteredProducts() {
            if (!this.searchQuery) {
                return this.availableProducts;
            }
            return this.availableProducts.filter(product =>
                product.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        },
    },
    watch: {
        hasPricingTableChanges(newVal) {
            this.$emit('has-changes', newVal);
        },
        setItems(Val) {
            if (!isEmpty(Val)) {
                this.tempAvailableProducts = JSON.parse(JSON.stringify(this.availableProducts))
                this.productList = []
                this.productListOptions = []
                this.pricingOptionCount = 1
                this.convertLeadProductsToSelectedProducts(Val.items)
                this.availableProducts = JSON.parse(JSON.stringify(this.tempAvailableProducts))
            }
        },
    },
    mounted() {
        this.pricingTableModal = new Modal(this.$refs.pricingTableModal);
        this.getProductList();
    },
    methods: {
        openModal() {
            this.pricingTableModal.show();
        },
        closeModal() {
            this.pricingTableModal.hide();
        },
        calculateOptionSubtotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0));
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionTotalPrice(productList) {
            return productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        calculateOptionGST(productList) {
            return productList.reduce((total, product) => {
                if (product.is_heading) return total;
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return total + productTotal;
            }, 0).toFixed(2);
        },

        toOptionName(count) {
            if (count <= 1) {
                return '';
            }

            let result = '';
            let num = count;

            while (num > 0) {
                let remainder = num % 26;

                if (remainder === 0) {
                    remainder = 26;
                }
                result = String.fromCharCode(64 + remainder) + result;
                num = Math.floor((num - remainder) / 26);
            }

            return result;
        },

        async getProductList() {
            try {
                const [pricingRes, authRes] = await Promise.allSettled([
                    this.$axios.get("/get-product-pricing"),
                    this.$axios.get("/auth-products")
                ]);

                this.availableProducts = [];

                if (pricingRes.status === "fulfilled" && pricingRes.value?.data?.productPrices) {
                    pricingRes.value.data.productPrices.forEach((p) => {
                        this.availableProducts.push({
                            id: p.ProductID,
                            authItemCode: p.AuthItemCode || "",
                            name: p.DisplayName,
                            defaultPricing: p.Price,
                            price: p.Price,
                            description: p.Description,
                            defaultQuantity: 1,
                            isCharityProduct: p.IsCharityProduct,
                            type: p.Type || "One-Off",
                            planType: p.ContractBasis || p.PricingPlanType || "",
                            forRenewal: p.ForRenewal || false,
                            childProducts: p.ChildProducts || []
                        });
                    });
                }

                if (authRes.status === "fulfilled" && authRes.value?.data?.authProducts) {
                    authRes.value.data.authProducts.forEach((authItem) => {
                        if (!authItem.Product) return;
                        const authCode = authItem.AuthItemCode || authItem.auth_item_code || authItem.product_id || authItem.Product.id || "";
                        const prodId = authItem.product_id || authItem.Product.id || "";
                        const exists = this.availableProducts.some(
                            (item) => (prodId && item.id === prodId) || (authCode && (item.authItemCode === authCode || item.id === authCode))
                        );
                        if (!exists) {
                            this.availableProducts.push({
                                id: prodId,
                                authItemCode: authCode,
                                name: authItem.Product.display_name || authItem.Product.name || "Product",
                                defaultPricing: authItem.Product.price ?? authItem.price ?? 0,
                                price: authItem.Product.price ?? authItem.price ?? 0,
                                description: authItem.Product.description || "",
                                defaultQuantity: 1,
                                isCharityProduct: authItem.is_charity_product === true || authItem.is_charity_product == 1,
                                type: authItem.Product.type || "One-Off",
                                planType: authItem.contract_type || authItem.contract_basis || "",
                                forRenewal: false,
                                childProducts: []
                            });
                        }
                    });
                }

                // Re-process setItems if it was set before products loaded (race condition fix)
                if (this.setItems && this.setItems.items && this.setItems.items.length > 0 && this.productList.length === 0) {
                    this.tempAvailableProducts = JSON.parse(JSON.stringify(this.availableProducts));
                    this.convertLeadProductsToSelectedProducts(this.setItems.items);
                    this.availableProducts = JSON.parse(JSON.stringify(this.tempAvailableProducts));
                }
            } catch (error) {
                if (error.response?.data?.message) {
                    this.$toast.error(error.response.data.message);
                }
            }
        },

        truncateText(text, limit) {
            if (!text) return '';
            if (text.length <= limit) return text;
            return text.substring(0, limit) + '...';
        },

        formatCurrency(value) {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return '$0.00';
            }
            return `$${numericValue.toFixed(2)}`;
        },
        calculateTotal(item) {
            return (item.quantity * item.pricing) - (item.discount * item.quantity);
        },
        calculateTotalDiscount(item) {
            return (item.discount * item.quantity);
        },
        addProduct(index) {
            this.isSelectingProduct = !this.isSelectingProduct;
            this.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        addProductOption(productOption, index) {
            productOption.isSelectingProduct = !productOption.isSelectingProduct;
            productOption.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        startEdit(index, field) {
            this.productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });
        },

        startEditOption(productList, index, field) {
            productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });

        },

        endEdit(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        endEditOption(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        selectProduct(selectedProduct) {
            log("product:", selectedProduct)

            const existingProductIndex = this.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const productName = (selectedProduct.name && selectedProduct.name.trim()) ? selectedProduct.name : (selectedProduct.product || "Product");
            const productDesc = (selectedProduct.description !== undefined && selectedProduct.description !== null && String(selectedProduct.description).trim() !== "") ? selectedProduct.description : (selectedProduct.Description || "");

            const newProduct = {
                id: selectedProduct.id,
                product: productName,
                name: productName,
                description: productDesc,
                remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                quantity: selectedProduct.defaultQuantity || selectedProduct.quantity || 1,
                discount: (selectedProduct.isCharityProduct && this.isCharityLead) ? selectedProduct.defaultPricing : (selectedProduct.defaultDiscount || selectedProduct.discount || 0.00),
                pricing: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                price: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                isEditingProduct: false,
                isEditingDescription: false,
                isEditingRemarks: false,
                isEditingQuantity: false,
                isEditingPricing: false,
                isEditingDiscount: false
            };

            if (existingProductIndex > -1) {
                this.productList.splice(existingProductIndex + 1, 0, newProduct);
            } else {
                this.productList.push(newProduct);
            }

            this.isSelectingProduct = false;
            this.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },

        selectProductOption(productOption, selectedProduct) {
            const existingProductIndex = productOption.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const productName = (selectedProduct.name && selectedProduct.name.trim()) ? selectedProduct.name : (selectedProduct.product || "Product");
            const productDesc = (selectedProduct.description !== undefined && selectedProduct.description !== null && String(selectedProduct.description).trim() !== "") ? selectedProduct.description : (selectedProduct.Description || "");

            const newProduct = {
                id: selectedProduct.id,
                product: productName,
                name: productName,
                description: productDesc,
                remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                quantity: selectedProduct.defaultQuantity || selectedProduct.quantity || 1,
                discount: (selectedProduct.isCharityProduct && this.isCharityLead) ? (selectedProduct.defaultDiscount > 0 ? selectedProduct.defaultDiscount : selectedProduct.defaultPricing) : (selectedProduct.defaultDiscount || selectedProduct.discount || 0.00),
                pricing: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                price: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                isEditingProduct: false,
                isEditingDescription: false,
                isEditingRemarks: false,
                isEditingQuantity: false,
                isEditingPricing: false,
                isEditingDiscount: false
            };

            if (existingProductIndex > -1) {
                productOption.productList.splice(existingProductIndex + 1, 0, newProduct);
            } else {
                productOption.productList.push(newProduct);
            }

            productOption.isSelectingProduct = false;
            productOption.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },


        removeProductOption(productOption, productList, indexToRemove) {

            if (productList.length == 1) {
                this.removeOption(productOption)
                return
            }

            if (indexToRemove > -1) {
                productList.splice(indexToRemove, 1);
            }

            this.hasPricingTableChanges = true;
        },

        addHeading(targetList) {
            targetList.push({
                id: "heading-" + Date.now() + "-" + Math.random().toString(36).substr(2, 7),
                product: "Section Heading",
                description: "",
                remarks: "",
                quantity: 0,
                discount: 0,
                pricing: 0,
                is_heading: true,
                isEditingProduct: true,
                isEditingDescription: false,
                isEditingRemarks: false,
                isEditingQuantity: false,
                isEditingPricing: false,
                isEditingDiscount: false
            });
            this.hasPricingTableChanges = true;
        },

        removeHeading(indexToRemove, targetList) {
            if (indexToRemove > -1 && indexToRemove < targetList.length) {
                targetList.splice(indexToRemove, 1);
                this.hasPricingTableChanges = true;
            }
        },

        canMoveUp(index, list) {
            return list && index > 0;
        },

        canMoveDown(index, list) {
            return list && index < list.length - 1;
        },

        moveItemUp(index, list) {
            if (!this.canMoveUp(index, list)) return;
            const temp = list[index];
            list[index] = list[index - 1];
            list[index - 1] = temp;
            this.hasPricingTableChanges = true;
        },

        moveItemDown(index, list) {
            if (!this.canMoveDown(index, list)) return;
            const temp = list[index];
            list[index] = list[index + 1];
            list[index + 1] = temp;
            this.hasPricingTableChanges = true;
        },

        removeProduct(indexToRemove) {
            const nonHeadingCount = this.productList.filter(p => !p.is_heading).length;
            if (nonHeadingCount <= 1) {
                alert("Cannot remove last product on table. Please add another one for replacement.")
                return
            }

            if (indexToRemove > -1) {
                this.productList.splice(indexToRemove, 1);
            }

            this.hasPricingTableChanges = true;
        },

        convertLeadProductsToSelectedProducts(products_array) {
            if (!isEmpty(products_array) && products_array.length > 0) {
                products_array.forEach((p, idx) => {
                    if (p.is_heading || p.isHeading || p.IsHeading) {
                        const headingItem = {
                            id: p.product_id || ("heading-" + Date.now() + "-" + Math.random().toString(36).substr(2, 7)),
                            product: (p.product_name && p.product_name.trim()) ? p.product_name : (p.product || "Section Heading"),
                            name: (p.product_name && p.product_name.trim()) ? p.product_name : (p.product || "Section Heading"),
                            description: p.description || "",
                            remarks: p.remarks || "",
                            quantity: 0,
                            discount: 0,
                            pricing: 0,
                            price: 0,
                            total: 0,
                            is_child: false,
                            is_heading: true,
                            parent_id: null,
                            option_index: p.option_index || 1,
                            line_item_index: p.line_item_index || (idx + 1),
                            isEditingProduct: false,
                            isEditingDescription: false,
                            isEditingRemarks: false,
                            isEditingQuantity: false,
                            isEditingPricing: false,
                            isEditingDiscount: false
                        };
                        if (p.option_index == 1) {
                            this.productList.push(headingItem);
                        } else if (p.option_index >= 2) {
                            while (this.productListOptions.length < p.option_index - 1) {
                                this.productListOptions.push({
                                    productList: [],
                                    isSelectingProduct: false,
                                    newProductIndex: -1
                                });
                            }
                            const option = this.productListOptions[p.option_index - 2];
                            option.productList.push(headingItem);
                        }
                        return;
                    }

                    const productFromList = this.availableProducts.find(
                        (item) => item.id === p.product_id || (item.authItemCode && item.authItemCode === p.product_id)
                    );

                    const resolvedName = (p.product_name && p.product_name.trim())
                        ? p.product_name
                        : (productFromList?.name || p.Product?.display_name || p.Product?.name || p.product || "Product");
                    const resolvedDesc = (p.description !== undefined && p.description !== null && String(p.description).trim() !== "")
                        ? p.description
                        : (productFromList?.description || p.Product?.description || "");
                    const resolvedRemarks = p.remarks || "";
                    const resolvedPrice = p.price !== undefined && p.price !== null
                        ? p.price
                        : (p.pricing !== undefined && p.pricing !== null ? p.pricing : (productFromList?.price !== undefined ? productFromList.price : (productFromList?.defaultPricing || 0)));
                    const resolvedQty = p.quantity !== undefined && p.quantity !== null ? p.quantity : 1;
                    const resolvedDiscount = p.discount !== undefined && p.discount !== null ? p.discount : 0;
                    const resolvedTotal = (resolvedPrice * resolvedQty) - resolvedDiscount;

                    const itemToPush = {
                        id: p.product_id || p.id,
                        name: resolvedName,
                        product: resolvedName,
                        description: resolvedDesc,
                        remarks: resolvedRemarks,
                        price: resolvedPrice,
                        pricing: resolvedPrice,
                        quantity: resolvedQty,
                        discount: resolvedDiscount,
                        total: resolvedTotal,
                        type: productFromList?.type || p.Product?.type || "One-Off",
                        planType: productFromList?.planType || p.Product?.contract_type || p.Product?.contract_basis || "",
                        forRenewal: productFromList?.forRenewal || false,
                        childProducts: productFromList?.childProducts || [],
                        is_child: p.is_child || false,
                        is_heading: false,
                        parent_id: p.parent_id || null,
                        option_index: p.option_index || 1,
                        line_item_index: p.line_item_index || (idx + 1),
                        isEditingProduct: false,
                        isEditingDescription: false,
                        isEditingRemarks: false,
                        isEditingQuantity: false,
                        isEditingPricing: false,
                        isEditingDiscount: false
                    };

                    if (p.is_charity) {
                        this.isCharityLead = true;
                    }

                    if (p.option_index == 1) {
                        this.productList.push(itemToPush);
                    } else if (p.option_index >= 2) {
                        while (this.productListOptions.length < p.option_index - 1) {
                            this.productListOptions.push({
                                productList: [],
                                isSelectingProduct: false,
                                newProductIndex: -1
                            });
                            this.pricingOptionCount++;
                        }
                        const option = this.productListOptions[p.option_index - 2];
                        option.productList.push(itemToPush);
                    }

                    this.hasPricingTableChanges = false;
                });
            }
        },

        addPricingOption() {

            let pO = [];

            if (!isEmpty(this.productList)) {
                this.productList.forEach(p => {
                    pO.push(JSON.parse(JSON.stringify(p)))
                })
            }

            this.productListOptions.push({
                productList: pO,
                isSelectingProduct: false,
                newProductIndex: -1
            })
            this.pricingOptionCount += 1
            this.hasPricingTableChanges = true;
        },

        removeOption(productOption) {
            let index = this.productListOptions.indexOf(productOption)
            this.productListOptions.splice(index, 1)
            this.hasPricingTableChanges = true;
        },

        rebuildSchedule() {
            let contract_items = []
            this.productList.forEach((p, idx) => {
                contract_items.push({
                    product_id: p.is_heading ? (p.id ? p.id.toString() : ("heading-" + Date.now())) : p.id.toString(),
                    product_name: p.product,
                    description: p.description || "",
                    price: p.is_heading ? 0 : p.pricing,
                    quantity: p.is_heading ? 0 : p.quantity,
                    discount: p.is_heading ? 0 : p.discount,
                    is_heading: p.is_heading || false,
                    line_item_index: idx + 1,
                    option_index: 1,
                    remarks: p.remarks || ""
                })
            })

            this.$emit('rebuild-schedule', contract_items);
            this.hasPricingTableChanges = false;
            this.needsScheduleRebuild = false;
        },
    }
}
</script>
<style scoped>
.pricing-modal-header {
    background-color: #00A0C1; /* Vertex Blue */
    color: white;
}

.pricing-modal-header .modal-title {
    color: white;
}

.pricing-modal-header .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
}
.product-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 1px solid #ddd;
}

.product-table th,
.product-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.product-table th {
    background-color: #00bcd4;
    color: white;
    font-weight: bold;
}

.product-table tr:last-child td {
    border-bottom: none;
}

.product-table td:first-child {
    align-items: center;
}

.product-table td:first-child>span {
    margin-right: 5px;
}

.add-product-row td {
    color: #00bcd4;
    cursor: pointer;
}

.remove-button {
    background-color: #ff9800;
    color: whitesmoke;
    border: 1px solid #ff9800;
}

.add-product-row td:first-child {
    font-weight: bold;
}

.add-product-row td:last-child {
    font-weight: bold;
    color: #ff8c00
}

.product-selection-list {
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    padding: 10px;
    margin-top: 5px;
    border-radius: 5px;
}

.product-item {
    padding: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.product-item:hover {
    background-color: #eee;
}

.product-table td input[type="number"] {
    width: 70px;
    padding: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9em;
    text-align: left;
}

.product-table td input[type="number"]:focus {
    outline: none;
    border-color: #00bcd4;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-selection-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.product-selection-table th,
.product-selection-table td {
    padding: 8px;
    border: 1px solid #ddd;
    text-align: left;
}

.product-selection-table th {
    background-color: #f2f2f2;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #f2f2f2;
}

.product-item {
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
}

.pricing-summary-footer {
    background: linear-gradient(180deg, rgba(0, 188, 212, 0.02) 0%, rgba(0, 188, 212, 0.08) 100%);
    border-top: 2px solid #00bcd4;
}
.pricing-summary-footer td {
    padding: 10px 16px;
    vertical-align: middle;
    border-bottom: 1px solid rgba(0, 188, 212, 0.1) !important;
}
.pricing-summary-footer .summary-label {
    text-align: right;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: #6c757d;
}
.pricing-summary-footer .summary-value {
    text-align: right;
    font-weight: 700;
    font-size: 1.05rem;
    color: #333;
}
.pricing-summary-footer .total-row td {
    border-bottom: none !important;
    padding-top: 14px;
    padding-bottom: 14px;
}
.pricing-summary-footer .total-label {
    color: #212529;
    font-size: 1.1rem;
    text-transform: uppercase;
}
.pricing-summary-footer .total-value {
    color: #00bcd4;
    font-size: 1.25rem;
}

.continue {
    background-color: #00bcd4;
    color: white;
}

.continue:disabled {
    background-color: lightgray;
    color: white;
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.button-select {
    padding: 5px 10px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
}

.orange-label-product-option {
    display: inline-block;
    background-color: #ff8c00;
    color: white;
    padding: 6px 12px;
    border-radius: 5px;
    font-weight: bold;
    font-size: 0.95em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.char-counter {
    display: block;
    text-align: right;
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 2px;
}

/* Two-column layout matching VertexProposal */
.pricing-two-column-layout {
    display: flex;
    gap: 20px;
    height: 100%;
}

.pricing-left-column {
    flex: 1;
    min-width: 0;
    overflow-x: auto;
}

.pricing-right-column {
    width: 320px;
    min-width: 280px;
    border-left: 1px solid #e0e0e0;
}

.remove-icon {
    color: #dc3545;
    cursor: pointer;
    font-size: 1.1em;
    transition: transform 0.2s, color 0.2s;
}

.remove-icon:hover {
    color: #a71d2a;
    transform: scale(1.2);
}

.product-search-input {
    width: 100%;
    padding: 10px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.add-btn-small {
    padding: 6px 12px;
    font-size: 0.9em;
}

.rebuild-schedule-btn {
    background-color: #ff9800;
    color: white;
}

.rebuild-schedule-btn:disabled {
    background-color: lightgray;
    color: white;
}

.remarks-input {
    width: 100%;
    padding: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9em;
}

.product-count-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    vertical-align: middle;
}

.product-count-badge i {
    font-size: 0.75rem;
}

.available-count-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 8px;
    padding: 0 2px;
}

.available-count-label i {
    font-size: 0.7rem;
    color: #00bcd4;
}

.product-name-cell {
    cursor: pointer;
}

.product-name-input {
    width: 100%;
    min-width: 120px;
    padding: 4px 8px;
    border: 1px solid #00bcd4;
    border-radius: 4px;
    font-size: 0.9em;
    font-weight: bold;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.4);
}
.reorder-btn-group {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    margin-right: 4px;
    vertical-align: middle;
}
.btn-reorder {
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    border-radius: 3px;
    color: #475569;
    font-size: 8px;
    line-height: 1;
    padding: 2px 4px;
    cursor: pointer;
    transition: all 0.15s ease;
}
.btn-reorder:hover:not(:disabled) {
    background: #00bcd4;
    border-color: #00bcd4;
    color: white;
}
.btn-reorder:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}
.heading-row {
    background-color: #f1f5f9;
}
.heading-name-input {
    font-size: 0.95rem;
    font-weight: 600;
    color: #0f172a;
    border: 1px solid #00bcd4;
    border-radius: 4px;
    padding: 2px 8px;
}
</style>
